import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField
} from '@mui/material';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { DataGrid, type GridPaginationModel } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import type { PreCompraBusqueda } from '../../stores/pre-compras/pre-compra';
import { usePreComprasStore } from '../../stores/pre-compras/pre-compra.store';
import { useUIStore } from '../../stores/ui/ui.store';
import ComboBox from '../ui/Combobox';

interface GridSortItem {
  field: string;
  sort: 'asc' | 'desc' | null | undefined;
}

interface PreCompraSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (registro: PreCompraBusqueda) => void;
}

const columnas: GridColDef[] = [
  {
    field: 'fecha',
    headerName: 'Fecha',
    width: 150,
    sortable: false,
    type: 'date',
    valueGetter: (value) => new Date(value)
  },
  { field: 'folioInterno', headerName: 'Folio', width: 150, sortable: true },
  { field: 'referencia', headerName: 'Referencia', flex: 1, sortable: true },
  { field: 'vendidoPor', headerName: 'Vendido Por', flex: 1, sortable: true }
];

const optionsFilter = [
  { label: 'Folio', value: 'folioInterno' },
  { label: 'Referencia', value: 'referencia' },
  { label: 'Vendido Por', value: 'vendidoPor' }
];

const PreCompraSearchDialog = ({ open, onClose, onSelect }: PreCompraSearchDialogProps) => {
  const [fechaIni, setFechaIni] = useState<Dayjs | null>(dayjs(new Date()));
  const [fechaFin, setFechaFin] = useState<Dayjs | null>(dayjs(new Date()));
  const [ordenamiento, setOrdenamiento] = useState<GridSortItem>({
    field: 'folioInterno',
    sort: 'asc'
  });
  const [columnaFiltro, setColumnaFiltro] = useState('folioInterno');
  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(0);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { loading } = useUIStore();
  const { list, cargarPaginadas } = usePreComprasStore();

  const cargaRegistros = async () => {
    cargarPaginadas({
      fechaDesde: fechaIni!.toDate(),
      fechaHasta: fechaFin!.toDate(),
      columnaFiltro: columnaFiltro,
      filtro: filtro,
      columnaOrden: ordenamiento.field,
      direccionOrden: ordenamiento.sort,
      limite: 10,
      pagina
    });
  };

  const handleCambioFiltro = (value: string) => {
    setFiltro(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {}, 500);
  };

  const handleCambioPagina = (model: GridPaginationModel) => {
    setPagina(model.page);
  };

  const handleCambioOrden = (model: GridSortModel) => {
    const [x] = model;
    setOrdenamiento(x);
  };

  useEffect(() => {
    if (open) {
      cargaRegistros();
    }
  }, [open, fechaIni, fechaFin, filtro, pagina, ordenamiento, columnaFiltro]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Búsqueda de Pre-Compras de Ganado</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
          <Paper variant="outlined" sx={{ padding: 2 }}>
            <Grid container direction="column" spacing={1}>
              <Grid>
                <Stack direction="row" spacing={2}>
                  <DatePicker label="Fecha inicial" value={fechaIni} onChange={setFechaIni} />
                  <DatePicker label="Fecha final" value={fechaFin} onChange={setFechaFin} />
                </Stack>
              </Grid>
              <Grid>
                <Grid container spacing={2}>
                  <Grid size="grow">
                    <TextField
                      label="Filtro"
                      placeholder="Comience a escribir para buscar..."
                      value={filtro}
                      onChange={(event) => handleCambioFiltro(event.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <ComboBox
                      label="Columna"
                      value={columnaFiltro}
                      onChange={(event) => setColumnaFiltro(event.target.value)}
                      options={optionsFilter}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
          <Paper sx={{ height: 350, width: '100%' }}>
            <DataGrid
              columns={columnas}
              rows={list.items}
              loading={loading}
              disableColumnResize
              paginationModel={{
                page: list.pageIndex,
                pageSize: list.pageSize
              }}
              getRowId={(row) => row.folioInterno}
              onPaginationModelChange={handleCambioPagina}
              onSortModelChange={handleCambioOrden}
              onRowDoubleClick={(model) => onSelect(model.row)}
              pageSizeOptions={[10]}
              rowCount={list.totalRows}
              paginationMode="server"
              autoHeight={false}
              disableColumnMenu
              disableRowSelectionOnClick
            />
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreCompraSearchDialog;
